import React from 'react';
import { Link } from 'react-router-dom';

function getReadableSwatchLabel(hex: string): string {
  const normalized = hex.replace('#', '');
  const r = parseInt(normalized.substring(0, 2), 16) / 255;
  const g = parseInt(normalized.substring(2, 4), 16) / 255;
  const b = parseInt(normalized.substring(4, 6), 16) / 255;
  const [R, G, B] = [r, g, b].map((v) =>
    v <= 0.03928 ? v / 12.92 : Math.pow((v + 0.055) / 1.055, 2.4)
  );
  const luminance = 0.2126 * R + 0.7152 * G + 0.0722 * B;
  return luminance > 0.55 ? '#111827' : '#F8FAFC';
}

export const SwatchChip: React.FC<{
  hex: string;
  title?: string;
  ringClass?: string;
}> = ({ hex, title, ringClass = 'ring-1 ring-black/10' }) => (
  <div
    className={`relative flex-1 min-w-[2.25rem] h-11 ${ringClass}`}
    style={{ backgroundColor: hex }}
    title={title}
  >
    <span
      className="absolute inset-0 flex items-center justify-center px-0.5 text-center text-[13px] font-mono"
      style={{ color: getReadableSwatchLabel(hex) }}
    >
      {hex}
    </span>
  </div>
);

export function ProductDesignPageHeader(props: {
  title: string;
  subtitle?: React.ReactNode;
  eyebrow?: string;
  /** Back-link target. Defaults to the design language front door. */
  backTo?: string;
  /** Back-link label (rendered as "← {backLabel}"). Defaults to "Design language". */
  backLabel?: string;
  /** Optional right-aligned slot in the breadcrumb row (e.g. a theme toggle). */
  actions?: React.ReactNode;
}) {
  const { title, subtitle, eyebrow, backTo = '/elevation/design-language', backLabel = 'Design language', actions } = props;
  return (
    <header className="mb-10 pb-8 border-b border-[var(--rule)]">
      <div className="mb-5 flex flex-wrap items-center gap-x-4 gap-y-2 font-mono text-[13px] font-medium uppercase tracking-[0.2em] text-[var(--text-ter)]">
        <Link to={backTo} className="transition-colors hover:text-[var(--text-pri)]">
          ← {backLabel}
        </Link>
        {eyebrow && (
          <>
            <span className="opacity-50" aria-hidden>
              ·
            </span>
            <span>{eyebrow}</span>
          </>
        )}
        {actions && <div className="ml-auto font-normal normal-case tracking-normal">{actions}</div>}
      </div>
      <h1 className="mb-3 font-serif text-3xl font-normal leading-[1.15] tracking-[-0.01em] text-[var(--text-pri)] sm:text-4xl">
        {title}
      </h1>
      {subtitle && <div className="max-w-3xl text-[15px] leading-relaxed text-[var(--text-sec)]">{subtitle}</div>}
    </header>
  );
}

export function ProductDesignSection(props: {
  title: string;
  children: React.ReactNode;
  hint?: React.ReactNode;
}) {
  return (
    <section className="mb-8 border border-[var(--rule)] p-6 md:p-8">
      <h2 className="mb-3 font-serif text-xl font-normal text-[var(--text-pri)]">{props.title}</h2>
      {props.hint && <p className="mb-5 text-sm leading-relaxed text-[var(--text-ter)]">{props.hint}</p>}
      {props.children}
    </section>
  );
}
